/**
 * With You API client.
 *
 * Wraps the saas-core /api/with-you/* endpoints. Uses apiFetch so it
 * works on both web (relative URLs) and Capacitor native (absolute
 * VITE_HORTON_API_URL).
 */

import { apiFetch } from '../config/api'

export type WithYouCadenceMode = 'off' | 'gentle' | 'regular' | 'frequent' | 'custom'

export interface WithYouPreferences {
  user_id?: number
  is_enabled: boolean
  cadence_mode: WithYouCadenceMode
  cadence_interval_minutes: number
  custom_interval_minutes?: number | null
  quiet_hours_start: string
  quiet_hours_end: string
  user_timezone: string
  allow_voice_default: boolean
  allow_push_notifications: boolean
  weekend_behavior: 'same' | 'reduced' | 'off'
  max_pushes_per_day: number
  minimum_spacing_minutes: number
  preferred_prompt_style: string
}

export interface WithYouSession {
  session_id: string | number
  chat_session_id: string | null
  started_at: string
}

export interface WithYouEntry {
  id: string | number
  session_id: string | number
  user_id: number
  entry_type: string
  raw_text: string | null
  created_at: string
  source: string
}

export interface WithYouNotification {
  id: string | number
  user_id: number
  sent_at: string
  template_key: string
  tone_mode: string
  message_text: string
  opened_at: string | null
  dismissed: boolean
}

async function jsonOrThrow<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`with_you ${resp.status}: ${text.slice(0, 200)}`)
  }
  return (await resp.json()) as T
}

export const withYouService = {
  async startSession(opts: {
    entry_source: 'push' | 'manual' | 'suggested' | 'followup'
    notification_id?: string
    timezone?: string
  }): Promise<WithYouSession> {
    const resp = await apiFetch('/api/with-you/session/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    })
    const data = await jsonOrThrow<{ success: boolean } & WithYouSession>(resp)
    return data
  },

  async recordEntry(opts: {
    session_id: string | number
    entry_type?: 'text' | 'voice' | 'text_and_voice' | 'quick_checkin'
    raw_text?: string
    source?: string
  }): Promise<WithYouEntry> {
    const resp = await apiFetch('/api/with-you/entry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entry_type: 'text', source: 'manual_open', ...opts }),
    })
    const data = await jsonOrThrow<{ success: boolean; entry: WithYouEntry }>(resp)
    return data.entry
  },

  async getFeed(date?: string): Promise<any[]> {
    const url = date ? `/api/with-you/feed?date=${encodeURIComponent(date)}` : '/api/with-you/feed'
    const resp = await apiFetch(url)
    const data = await jsonOrThrow<{ success: boolean; items: any[] }>(resp)
    return data.items
  },

  async getPreferences(): Promise<WithYouPreferences> {
    const resp = await apiFetch('/api/with-you/preferences')
    const data = await jsonOrThrow<{ success: boolean; preferences: WithYouPreferences }>(resp)
    return data.preferences
  },

  async updatePreferences(updates: Partial<WithYouPreferences>): Promise<WithYouPreferences> {
    const resp = await apiFetch('/api/with-you/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    const data = await jsonOrThrow<{ success: boolean; preferences: WithYouPreferences }>(resp)
    return data.preferences
  },

  async listNotifications(): Promise<WithYouNotification[]> {
    const resp = await apiFetch('/api/with-you/notifications')
    const data = await jsonOrThrow<{ success: boolean; items: WithYouNotification[] }>(resp)
    return data.items
  },

  async markNotificationOpened(notificationId: string | number): Promise<boolean> {
    const resp = await apiFetch(`/api/with-you/notifications/${notificationId}/opened`, {
      method: 'POST',
    })
    const data = await jsonOrThrow<{ success: boolean }>(resp)
    return data.success
  },
}

// ── Quiet days — the one conditioned check-in (2026-09-15) ─────────────────
// saas-core asks it after ≥2 quiet days; the motivator pushes it with a deep
// link to /today?ask=quiet-days; the card on /today takes the answer.

export type QuietDaysAnswer = 'lighten' | 'new_priority' | 'all_good'

export interface QuietDaysCheckin {
  id: number | string
  asked_at: string
  local_date: string
  quiet_days: number
  message_text: string
  with_you_notification_id?: number | string | null
  answered_at?: string | null
  answer?: QuietDaysAnswer | null
}

export async function fetchQuietDaysCheckin(): Promise<QuietDaysCheckin | null> {
  const resp = await apiFetch('/api/with-you/quiet-days/pending')
  if (!resp.ok) return null
  const json = await resp.json()
  return (json?.checkin as QuietDaysCheckin | null) ?? null
}

export async function answerQuietDaysCheckin(
  id: number | string, answer: QuietDaysAnswer,
): Promise<{ words: string }> {
  const resp = await apiFetch(`/api/with-you/quiet-days/${id}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answer }),
  })
  if (!resp.ok) throw new Error(`answer failed: ${resp.status}`)
  const json = await resp.json()
  return { words: String(json?.words ?? '') }
}

// ── Cadence, conditioned on the rhythm (2026-09-15) ──────────────────────────
export interface WithYouCadence {
  ok: boolean; reason: string
  window: { start: number; end: number; source: 'rhythm' | 'profile'; signals: number }
  local_hour: number; timezone: string | null
  around_today: boolean; sent_today: number; yesterday_unopened: boolean; pending_checkin: boolean
  words: string[]
}

export async function fetchWithYouCadence(): Promise<WithYouCadence | null> {
  const resp = await apiFetch('/api/with-you/cadence')
  if (!resp.ok) return null
  const json = await resp.json()
  return json?.success ? (json as WithYouCadence) : null
}
